import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/** Normalize common markdown so chat shows clean readable text (not raw ** markers). */
export function normalizeVysorMarkdown(input: string): string {
  let text = String(input || '').replace(/\r\n/g, '\n');
  // Headings → plain emphasis lines
  text = text.replace(/^#{1,6}\s+/gm, '');
  // __bold__ → **bold**
  text = text.replace(/__([^_\n]+)__/g, '**$1**');
  // Collapse ****empty**** / stray pairs
  text = text.replace(/\*\*\s*\*\*/g, '');
  return text;
}

/** Inline **bold**, `code`, and bare URLs inside one line of bot chat text. */
export function renderVysorInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+?\*\*|`[^`]+`|https?:\/\/[^\s<>"']+)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+?\*\*$/.test(part)) {
      return (
        <strong key={i} style={{ fontWeight: 700 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (/^`[^`]+`$/.test(part)) {
      return (
        <Box
          key={i}
          component="code"
          sx={{
            px: 0.5,
            py: 0.1,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.92em',
          }}
        >
          {part.slice(1, -1)}
        </Box>
      );
    }
    if (/^https?:\/\//.test(part)) {
      const href = part.replace(/[.,;:!?)]+$/, '');
      const trail = part.slice(href.length);
      return (
        <React.Fragment key={i}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecorationLine: 'underline', wordBreak: 'break-all' }}
          >
            {href}
          </a>
          {trail}
        </React.Fragment>
      );
    }
    // Strip any leftover unpaired ** so users never see raw markers
    const cleaned = part.replace(/\*\*/g, '');
    return <React.Fragment key={i}>{cleaned}</React.Fragment>;
  });
}

/** Render multi-line VYSOR bot messages (bullets, numbered steps, paragraphs, bold). */
export function renderVysorBotContent(content: string, fontSize = '0.82rem'): React.ReactNode {
  let normalized = normalizeVysorMarkdown(content);
  if (!/\n/.test(normalized) && /\s+\d+\.\s+/.test(normalized)) {
    normalized = normalized.replace(/\s+(\d+\.\s+)/g, '\n$1');
  }
  if (!/\n/.test(normalized) && /\s+[A-Z]\.\s+[A-Z]/.test(normalized)) {
    normalized = normalized.replace(/\s+([A-Z]\.\s+)/g, '\n$1');
  }

  const lines = normalized.split('\n');
  const elements: React.ReactNode[] = [];

  lines.forEach((line, idx) => {
    if (/^[•\-\*]\s+/.test(line)) {
      const text = line.replace(/^[•\-\*]\s+/, '');
      elements.push(
        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.3, pl: 0.5 }}>
          <Typography component="span" sx={{ mr: 0.5, mt: '1px', lineHeight: 1.5, fontSize }}>
            •
          </Typography>
          <Typography component="span" sx={{ lineHeight: 1.5, fontSize }}>
            {renderVysorInline(text)}
          </Typography>
        </Box>,
      );
    } else if (/^\d+\.\s+/.test(line.trim())) {
      const m = line.trim().match(/^(\d+\.)\s+(.*)$/);
      elements.push(
        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.45, pl: 0.5 }}>
          <Typography
            component="span"
            sx={{ mr: 0.6, fontWeight: 700, lineHeight: 1.5, fontSize, minWidth: '1.4em' }}
          >
            {m?.[1]}
          </Typography>
          <Typography component="span" sx={{ lineHeight: 1.5, fontSize }}>
            {renderVysorInline(m?.[2] || '')}
          </Typography>
        </Box>,
      );
    } else if (/^[A-Z]\.\s+/.test(line.trim())) {
      elements.push(
        <Typography
          key={idx}
          component="div"
          sx={{ lineHeight: 1.5, mb: 0.35, mt: idx ? 0.6 : 0, fontWeight: 700, fontSize }}
        >
          {renderVysorInline(line.trim())}
        </Typography>,
      );
    } else if (line.trim() === '') {
      elements.push(<Box key={idx} sx={{ height: 8 }} />);
    } else {
      elements.push(
        <Typography
          key={idx}
          component="div"
          sx={{ lineHeight: 1.5, mb: 0.25, fontSize, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {renderVysorInline(line)}
        </Typography>,
      );
    }
  });

  const opens = [
    { test: /pending sso|sso review/i, to: '/sso-review?pipeline=pending', label: 'Open pending SSO review' },
    { test: /expired grant|ai governance/i, to: '/ai-governance', label: 'Open AI governance' },
    { test: /overdue|privileged/i, to: '/pam-dashboard', label: 'Open privileged accounts' },
  ].filter((item) => item.test.test(normalized));

  return (
    <>
      {elements}
      {opens.map((item) => (
        <Button
          key={item.to}
          component={RouterLink}
          to={item.to}
          size="small"
          variant="outlined"
          sx={{ mt: 1, mr: 1, textTransform: 'none' }}
        >
          {item.label}
        </Button>
      ))}
    </>
  );
}
